import { cn } from "@/lib/utils";
import { memo, useCallback, useEffect, useMemo } from "react";
import { TableRow } from "@/components/ui/table";
import { Row } from "@tanstack/react-table";
import DataTableCell from "./DataTableCell";
import { VirtualItem } from "@tanstack/react-virtual";
import useTableColumnVisibility from "../hooks/useTableColumnVisibility";
import useTableColumnPinning from "../hooks/useTableColumnPinning";
import { useDataTableSettings } from "../hooks/useDataTableSettings";

interface DataTableRowProps<TData extends { id: string }> {
  row: Row<TData>;
  virtualRow: VirtualItem;
  rowClassName?: string;
  getRowProps?: <T>(row: { original: T }) => Record<string, unknown>;
  onRowMouseEnterInternal: (rowId: string) => void;
  onRowMouseLeaveInternal: (rowId: string) => void;
}

function DataTableRow<TData extends { id: string }>({
  row,
  rowClassName,
  getRowProps,
  virtualRow,
  onRowMouseEnterInternal,
  onRowMouseLeaveInternal,
}: DataTableRowProps<TData>) {
  const dynamicRowProps = useMemo(() => {
    return getRowProps?.(row) || {};
  }, [getRowProps, row]);

  const { firstVisibleColumnId } = useDataTableSettings<TData>();

  const style = useMemo(() => {
    const baseStyle: React.CSSProperties = {
      transform: `var(--row-transform-${virtualRow.index})`,
      ...(dynamicRowProps.style || {}),
    };

    if (!getRowProps) {
      baseStyle.backgroundColor = `var(--row-bg-${row.id})`;
    }

    return baseStyle;
  }, [virtualRow.index, row.id, dynamicRowProps.style, getRowProps]);

  const onMouseEnter = useCallback(() => {
    onRowMouseEnterInternal(row.id);
  }, [onRowMouseEnterInternal, row.id]);

  const onMouseLeave = useCallback(() => {
    onRowMouseLeaveInternal(row.id);
  }, [onRowMouseLeaveInternal, row.id]);

  const { columnVisibility } = useTableColumnVisibility();
  const { columnPinning } = useTableColumnPinning();

  const combinedClassName = useMemo(() => {
    return cn(
      "flex absolute w-full",
      rowClassName,
      dynamicRowProps.className as string,
    );
  }, [rowClassName, dynamicRowProps.className]);

  useEffect(() => {
    // TODO: This is a hack to force the row to re-render when the column visibility/pinning changes
    // We need to find a better way to do this
  }, [columnVisibility, columnPinning]);

  return (
    <TableRow
      key={row.id}
      {...dynamicRowProps}
      data-state={row.getIsSelected() && "selected"}
      className={combinedClassName}
      style={style}
      data-index={virtualRow.index}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {row.getVisibleCells().map((cell) => {
        return (
          <DataTableCell
            key={cell.id}
            cell={cell}
            firstVisibleColumnId={firstVisibleColumnId}
          />
        );
      })}
    </TableRow>
  );
}

export default memo(DataTableRow) as <TData extends { id: string }>(
  props: DataTableRowProps<TData>,
) => JSX.Element;
